import React, { useState } from 'react'

import { useCreateDevices } from '../hooks/useCreateDevices'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Modal, Tabs } from '@hivewatch/dark'

import {
  useCreateAlarmMutation,
  useCreateCameraMutation,
  useCreateDoorMutation,
} from '@/graphql/generated/hooks'
import {
  CreateAlarmInput,
  CreateCameraInput,
  CreateDoorInput,
  DeviceType,
} from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

import { BulkUploadForm } from '../components/BulkUploadForm'
import { FormInputsIProps } from '../types/types'
import { CSV_UploadStatus } from '../utils/csvParser'
import { CSVParseResultI, VALID_DEVICES } from '../utils/csvValidator'
import { DarkCreateDeviceForm } from './DarkCreateDeviceForm'

interface DarkCreateDeviceModalIProps {
  isOpen: boolean
  onClose: () => void
}

const INDIVIDUAL_TAB = 'individual'
const BULK_UPLOAD_TAB = 'bulk'

interface CreateDeviceInputBaseI {
  name: string
  externalId?: string
  floorId: string
  label?: string
}

/**
 * Rebrand variant of {@link CreateDeviceModal} built on `@hivewatch/dark`. The
 * per-device-type mutations, the refetch set, the row-level bulk status
 * tracking and the `updatedDeviceBulkUpload` gating are all unchanged.
 */
export const DarkCreateDeviceModal = ({
  isOpen,
  onClose,
}: DarkCreateDeviceModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableCreateDevice } = usePermissions()
  const isUpdatedBulkUpload = useFeatureFlag(
    FeatureFlag.updatedDeviceBulkUpload
  )
  const form = useForm<FormInputsIProps>({ mode: 'onChange' })
  const { handleSubmit, watch, formState } = form
  const [parseResult, setParseResult] = useState<CSVParseResultI | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [bulkUploadResetKey, setBulkUploadResetKey] = useState(0)
  const [createAlarm] = useCreateAlarmMutation()
  const [createDoor] = useCreateDoorMutation()
  const [createCamera] = useCreateCameraMutation()
  const { showSuccess, showError } = useToasts({ preventDuplicate: false })

  const { isValid } = formState
  const [activeTab, setActiveTab] = useState(INDIVIDUAL_TAB)
  const isBulkUploadSelected = activeTab === BULK_UPLOAD_TAB
  const {
    create,
    setFacilityName,
    facilityOptions,
    floorOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    isIncidentTypesLoading,
    incidentTypeOptions,
    isOptionsLoading,
    isLoading,
  } = useCreateDevices({
    onClose,
    facilityId: watch('facility')?.value,
    isBulkUploadSelected,
  })

  const pendingRows =
    parseResult?.data.filter(
      (d) =>
        d.uploadStatus === CSV_UploadStatus.Pending || d.uploadStatus == null
    ) ?? []

  const isBulkUploadActive = isUpdatedBulkUpload && isBulkUploadSelected
  const isBulkFormInvalid = !parseResult || pendingRows.length === 0
  const isFormInvalid = isBulkUploadActive ? isBulkFormInvalid : !isValid
  const isSubmitDisabled = !shouldEnableCreateDevice || isFormInvalid
  const isBulkBusy = isCreating || isParsing
  const isBusy = isBulkUploadActive ? isBulkBusy : isLoading

  const handleBulkCreate = async () => {
    if (!parseResult || pendingRows.length === 0) {
      showError(t('devices:crud_create_no_devices_error'))
      return
    }
    setIsCreating(true)
    const results = {
      successful: [] as string[],
      failed: [] as { name: string; error: string }[],
    }
    const devicesWithStatus = parseResult.data.map((device) => ({
      ...device,
      uploadStatus:
        device.uploadStatus === CSV_UploadStatus.Failed ||
        device.uploadStatus === CSV_UploadStatus.Success
          ? device.uploadStatus
          : CSV_UploadStatus.Pending,
    }))
    setParseResult({ ...parseResult, data: devicesWithStatus })
    const updatedDevices = [...devicesWithStatus]

    for (let i = 0; i < updatedDevices.length; i++) {
      const device = updatedDevices[i]
      if (device.uploadStatus !== CSV_UploadStatus.Pending) continue
      const base: CreateDeviceInputBaseI = {
        name: device.name,
        floorId: device.floorId,
        externalId: device.externalId,
        label: device.label,
      }
      try {
        const refetchQueries = [
          'Devices',
          'FloorDevicesCount',
          'FacilityDevicesByFloors',
        ]
        switch (device.deviceType) {
          case DeviceType.Alarm: {
            const alarmInput = {
              ...base,
              ...(device.alarmTypeId
                ? { alarmTypeId: device.alarmTypeId }
                : {}),
            } as CreateAlarmInput
            await createAlarm({
              variables: { alarmInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          case DeviceType.Door: {
            const doorInput: CreateDoorInput = { ...base }
            await createDoor({
              variables: { doorInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          case DeviceType.Camera: {
            const cameraInput: CreateCameraInput = {
              ...base,
              macAddress: device.macAddress,
              ip: device.ip,
              rtspPath: device.rtspPath,
              rtspPort: device.rtspPort,
              streamingProtocol: device.streamProtocol,
              streamingType: device.streamType,
            }
            await createCamera({
              variables: { cameraInput },
              refetchQueries: refetchQueries,
            })
            break
          }
          default:
            throw Error(
              `Device Type: ${device.deviceType} is not one of ${VALID_DEVICES}`
            )
        }
        results.successful.push(device.name)
        updatedDevices[i] = {
          ...device,
          uploadStatus: CSV_UploadStatus.Success,
        }
      } catch (e) {
        results.failed.push({ name: device.name, error: e.message })
        updatedDevices[i] = {
          ...device,
          uploadStatus: CSV_UploadStatus.Failed,
          uploadError: e.message,
        }
      }
      setParseResult((prev) => ({ ...prev, data: [...updatedDevices] }))
    }

    setIsCreating(false)

    if (results.successful.length > 0) {
      const count = results.successful.length
      showSuccess(
        tVar(t, 'devices:crud_create_success', {
          count,
          unit:
            count === 1
              ? t('devices:crud_unit_device')
              : t('devices:crud_unit_devices'),
        })
      )
    }

    const totalFailed = updatedDevices.filter(
      (d) => d.uploadStatus === CSV_UploadStatus.Failed
    ).length
    if (totalFailed > 0) {
      showError(
        tVar(t, 'devices:crud_create_error_count', {
          count: totalFailed,
          unit:
            totalFailed === 1
              ? t('devices:crud_unit_device')
              : t('devices:crud_unit_devices'),
        })
      )
    }
  }

  const handleClose = () => {
    if (!isBusy) {
      onClose()
    }
  }

  const handleFormSubmit = async () => {
    if (isBulkUploadActive) {
      await handleBulkCreate()
    } else {
      await handleSubmit(create)()
    }
  }

  const handleTabChange = (id: string) => {
    if (isBusy) return
    setActiveTab(id)
    if (isUpdatedBulkUpload && !isCreating) {
      setParseResult(null)
      form.setValue('file', null)
      setBulkUploadResetKey((k) => k + 1)
    }
  }

  // Widen the modal while the parsed-device table is on screen.
  const maxW =
    isUpdatedBulkUpload && parseResult?.data?.length ? '90vw' : '700px'

  // Both tabs lock while work is in flight: switching away from a running bulk
  // upload would drop `isBulkUploadActive`, and with it `isBusy`, re-enabling
  // the close control while row mutations are still executing.
  const tabItems = [
    {
      disabled: isBusy,
      id: INDIVIDUAL_TAB,
      label: t('devices:crud_create_tab_individual'),
    },
    {
      disabled: isBusy,
      id: BULK_UPLOAD_TAB,
      label: t('devices:crud_create_tab_bulk'),
    },
  ]

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnOverlayClick={!isBusy}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={isSubmitDisabled}
      isSaveLoading={isBusy}
      maxW={maxW}
      onClose={handleClose}
      onSave={handleFormSubmit}
      saveButtonLabel={t('devices:crud_create_title')}
      showCancelButton
      title={t('devices:crud_create_title')}
    >
      <Tabs
        activeId={activeTab}
        colorScheme={colorScheme}
        items={tabItems}
        onChange={handleTabChange}
      />
      <Box mt='16px'>
        {isBulkUploadSelected ? (
          <BulkUploadForm
            colorScheme={colorScheme}
            form={form}
            isRemoveDisabled={isCreating || isParsing}
            key={isUpdatedBulkUpload ? bulkUploadResetKey : undefined}
            onIsParsingChange={isUpdatedBulkUpload ? setIsParsing : undefined}
            onParseResultChange={
              isUpdatedBulkUpload ? setParseResult : undefined
            }
            parseResult={isUpdatedBulkUpload ? parseResult : undefined}
          />
        ) : (
          <DarkCreateDeviceForm
            colorScheme={colorScheme}
            facilityOptions={facilityOptions}
            floorOptions={floorOptions}
            form={form}
            incidentTypeOptions={incidentTypeOptions}
            isFacilitiesError={isFacilitiesError}
            isFacilitiesLoading={isFacilitiesLoading}
            isIncidentTypesLoading={isIncidentTypesLoading}
            isOptionsLoading={isOptionsLoading}
            setFacilityName={setFacilityName}
          />
        )}
      </Box>
    </Modal>
  )
}
